import { useState } from 'preact/hooks';
import Decimal from 'decimal.js';
import { useAppState, buildTFSAState } from '../lib/state';
import { availableRoom, restoringNextJan, runningPenalty, eligibleStartYear } from '../engine/tfsa';
import { formatCAD, centsToDecimal } from '../lib/decimal';
import { localIsoNow, currentYearLocal } from '../lib/timezone';
import { StatCard } from '../components/StatCard';
import { ButtonPrimary } from '../components/ButtonPrimary';
import { ButtonSecondary } from '../components/ButtonSecondary';
import { LogEntryModal } from '../components/LogEntryModal';
import { PenaltyBanner } from '../components/PenaltyBanner';
import { Simulator } from '../components/Simulator';
import { PreviewCard } from '../components/PreviewCard';

export function Dashboard() {
  const { state, dispatch } = useAppState();
  const [logModalMode, setLogModalMode] = useState<'contribution' | 'withdrawal' | null>(null);

  const today = localIsoNow();
  const tfsa = buildTFSAState(state, today);

  if (!tfsa) return null;

  const available = availableRoom(tfsa);
  const restoring = restoringNextJan(tfsa);

  // P0-1: Running CRA over-contribution penalty. Only rendered when
  // the engine reports a non-zero accumulated penalty (which requires
  // both a prior_year_excess_cents > 0 AND at least one month elapsed).
  const penalty = runningPenalty(tfsa, today.split('T')[0]!);
  const showPenaltyBanner =
    new Decimal(penalty.penalty_cents).greaterThan(0);

  // P1-3: Contributions YTD must reflect ONLY the current calendar year.
  // Prior-year entries (e.g. 2025 contributions logged for history) must
  // not inflate the "Contrib 2026" stat. Year is read from the local-clock
  // ISO via currentYearLocal (timezone-correct per Lock 4).
  const currentYear = currentYearLocal(today);
  const contribsYtdCents = tfsa.contributions
    .filter((c) => currentYearLocal(c.date) === currentYear)
    .reduce((sum, c) => sum.plus(new Decimal(c.amount_cents)), new Decimal(0))
    .toFixed(0);

  const isEstimate = tfsa.cra_room_method === 'estimate';
  const recentEntries = [...state.logEntries].reverse().slice(0, 3);

  return (
    <div class="max-w-md mx-auto px-4 py-6 space-y-4">
      {/* P0-1: Running over-contribution penalty banner (above stat cards) */}
      {showPenaltyBanner && (
        <PenaltyBanner
          excess_cents={tfsa.prior_year_excess_cents ?? '0'}
          penalty_cents={penalty.penalty_cents}
          months_over={penalty.months_over}
        />
      )}

      {/* Estimate warning */}
      {isEstimate && (
        <p class="text-xs bg-cream border border-grain-gold rounded px-3 py-2 flex items-center gap-2 text-body/70">
          <span aria-hidden="true">⚠</span>
          <span>Estimate — verify with CRA before contributing large amounts.</span>
        </p>
      )}

      {/* Three-stat hero (Q4 lock) */}
      <div class="space-y-3">
        <StatCard label="Available Room" value={formatCAD(available)} highlight>
          <span data-testid="stat-available-room" class="sr-only">{formatCAD(available)}</span>
        </StatCard>
        {/* P1-2: Eligibility-start-year explanation. Demystifies the
            cumulative-room math by showing the year CRA started crediting
            this user with TFSA room (max of birth_year+18, residency_year,
            and the 2009 program-start year). */}
        {state.wizardInputs && (
          <p class="text-xs text-body/70 px-1">
            You've been TFSA-eligible since{' '}
            {eligibleStartYear(
              state.wizardInputs.birth_year,
              state.wizardInputs.residency_year
            )}
            .
          </p>
        )}
        <div class="grid grid-cols-2 gap-3">
          <StatCard label="Contrib 2026" value={formatCAD(contribsYtdCents)} />
          <StatCard label="Restoring Jan 1" value={formatCAD(restoring)} />
        </div>
      </div>

      {/* Action buttons */}
      <div class="grid grid-cols-2 gap-3">
        <ButtonPrimary onClick={() => setLogModalMode('contribution')}>
          + Contribution
        </ButtonPrimary>
        <ButtonSecondary onClick={() => setLogModalMode('withdrawal')}>
          − Withdrawal
        </ButtonSecondary>
      </div>

      {/* Recent activity / empty state (C.12) */}
      <section aria-label="Recent activity">
        <div class="flex items-center justify-between mb-2">
          <h2 class="text-xs uppercase tracking-wider text-body/70 font-semibold">
            Recent activity
          </h2>
          <a
            href="/log"
            class="text-xs text-hudson-navy underline hover:text-hudson-navy/80 focus:outline-none focus:ring-2 focus:ring-hudson-navy rounded"
          >
            View all →
          </a>
        </div>
        {recentEntries.length === 0 ? (
          <div class="text-center py-8 border border-dashed border-card-border rounded-lg">
            <p class="text-sm text-body/70">No activity yet</p>
            <p class="text-xs text-body/70 mt-1">
              Log your first contribution or withdrawal above.
            </p>
          </div>
        ) : (
          <ul class="divide-y divide-card-border border border-card-border rounded-lg overflow-hidden">
            {recentEntries.map((e) => {
              // v1.0.4 P0 (defect B): display abs magnitude; sign is "+"
              // only for a positive contribution. See History.tsx for the
              // long-form rationale.
              const cents = centsToDecimal(e.amount_cents);
              const sign = e.type === 'contribution' && cents.greaterThan(0) ? '+' : '−';
              const text = formatCAD(cents.abs().toFixed(0));
              return (
                <li
                  key={e.id}
                  class="flex items-center justify-between px-4 py-3 bg-white text-sm"
                >
                  <span class="text-body/70">{e.date}</span>
                  <span
                    class={`font-medium ${
                      e.type === 'contribution' ? 'text-success-green' : 'text-alert-red'
                    }`}
                  >
                    <span aria-hidden="true">{sign}</span>
                    {text}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Simulator (C.8) */}
      <Simulator tfsa={tfsa} />

      {/* Preview cards (C.14) */}
      <PreviewCard />

      {/* Log entry modal (C.6) */}
      {logModalMode && (
        <LogEntryModal
          mode={logModalMode}
          tfsa={tfsa}
          onClose={() => setLogModalMode(null)}
          onSubmit={(entry) => {
            dispatch({ type: 'ADD_ENTRY', entry });
            setLogModalMode(null);
          }}
        />
      )}
    </div>
  );
}
